import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "react-redux";

import { addNotification } from "fond/notifications/redux";
import { usePolygonSelect } from "fond/project/panels/polygon/usePolygonSelect";
import { PolygonState, selectUploadOption } from "fond/project/polygon/redux";
import { closePolygonSelect } from "fond/project/redux";
import { openBaseDataFlyout } from "fond/redux/baseDataFlyout";
import { dismissBaseDataTask, selectBaseDataTaskRecords, startBaseDataTask } from "fond/redux/baseDataTasks";
import { Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BOUNDARY_SAVE_ERROR } from "../copy";
import { useBaseDataModelContext } from "../useBaseDataModel";

import { isReadingPolygonFile, POLYGON_FILE_STATES, selectIsBoundarySelectOpen } from "./boundary";
import PolygonFileUpload from "./PolygonFileUpload";
import ReplaceConfirmation from "./ReplaceConfirmation";
import TaskProgress from "./TaskProgress";
import { useBeginBoundarySelect, useSaveBoundary } from "./useBoundary";

const TITLE = "Uploading boundary";

interface BoundaryUploadProps {
  /** Whether the version already has a boundary, which the upload replaces. */
  hasBoundary: boolean;
  /** Called when the user backs out of the upload. */
  onCancel(): void;
}

/**
 * The Boundary row's Upload option: upload a polygon file and save it as the boundary straight away.
 */
const BoundaryUpload: React.FC<BoundaryUploadProps> = ({ hasBoundary, onCancel }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const polygonSelect = usePolygonSelect();
  const beginBoundarySelect = useBeginBoundarySelect();
  const { saveBoundary } = useSaveBoundary();
  const isActive = useAppSelector(selectIsBoundarySelectOpen);
  const ownTask = useBaseDataModelContext().rows.boundary.task;
  const task = ownTask?.kind === "boundaryUpload" ? ownTask : null;
  const [isConfirming, setIsConfirming] = useState(false);
  const isConverted = isActive && polygonSelect.polygon.polygonState === PolygonState.complete;
  const isReading = task?.status === "running" && task.stage === "readingFile";

  // Leave polygon select when the option closes, unless it is being used to draw or import; an upload still reading its file stops.
  useEffect(
    () => () => {
      const state = store.getState();
      if (selectIsBoundarySelectOpen(state) && POLYGON_FILE_STATES.includes(state.project.polygon.polygonState)) {
        dispatch(closePolygonSelect());
      }
      if (isReadingPolygonFile(selectBaseDataTaskRecords(state).boundary, "boundaryUpload")) dispatch(dismissBaseDataTask("boundary"));
    },
    [dispatch, store]
  );

  const save = useCallback(async () => {
    try {
      await saveBoundary(store.getState().project.polygon.selectedArea, "boundaryUpload");
    } catch {
      return;
    }
    dispatch(closePolygonSelect());
    dispatch(addNotification("Boundary saved."));
    dispatch(openBaseDataFlyout({ target: "boundary", expanded: "importWithinBoundary" }));
  }, [dispatch, saveBoundary, store]);

  useEffect(() => {
    if (isReading && isConverted) save();
  }, [isConverted, isReading, save]);

  const upload = () => {
    setIsConfirming(false);
    dispatch(startBaseDataTask({ key: "boundary", source: "boundaryUpload", keys: ["boundary"], stage: "readingFile" }));
    beginBoundarySelect();
    dispatch(selectUploadOption());
    polygonSelect.uploadPolygon();
  };

  const cancel = () => {
    if (isActive) dispatch(closePolygonSelect());
    if (polygonSelect.upload) polygonSelect.dismissPolygonUploadError();
    if (task) dispatch(dismissBaseDataTask("boundary"));
    setIsConfirming(false);
    onCancel();
  };

  if (isConfirming) {
    return <ReplaceConfirmation layerNoun="boundary" onCancel={() => setIsConfirming(false)} onReplace={upload} />;
  }

  if (task?.status === "failed") {
    return (
      <TaskProgress
        error={{
          title: "Couldn't save the boundary",
          reason: BOUNDARY_SAVE_ERROR,
          actions: [
            { label: "Cancel", onClick: cancel },
            // The uploaded polygon is kept only while polygon select stays open.
            ...(isConverted ? [{ label: "Retry", onClick: save, primary: true }] : []),
          ],
        }}
      />
    );
  }

  if (task?.status === "running") return <TaskProgress title={TITLE} phase={task.phase} />;

  return <PolygonFileUpload polygonSelect={polygonSelect} onUpload={hasBoundary ? () => setIsConfirming(true) : upload} onCancel={cancel} />;
};

export default BoundaryUpload;
